import { Link as RouterLink } from 'react-router-dom';
import { Chip, Tooltip } from '@mui/material';
import type { TriageCardGroup } from '../../types/notifications.types';
import { GROUP_PARAM } from '../entry/groupFilter';

/** The opener page the chip links into — its own route, its own `group` key. */
const OPENER_PAGE = '/predictions/opening';

/**
 * The opener group a triage card belongs to.
 *
 * Every state is spelled out, because the three of them mean different things
 * to whoever is triaging and collapsing them would send someone to the wrong
 * screen:
 *
 * - a NAMED group → the group's own name, linking to its opener tab;
 * - a group id with no name → the group was deleted; the id still renders so
 *   the card is traceable, and the tooltip says so;
 * - no group at all → *ungrouped*, which for an orphaned fill is the common
 *   case (it names no rule, so it is placed only when every entry rule on its
 *   pair agrees on one group).
 *
 * It NEVER guesses. A mark on the wrong group is worse than no mark: it sends
 * the operator to look for a naked leg on a screen where it is not.
 */
export function TriageGroupChip({ group }: { group?: TriageCardGroup | null }) {
  const id = group?.id ?? null;
  const name = group?.name ?? null;

  if (!id) {
    return (
      <Tooltip title="This card could not be placed on exactly one opener group — an ungrouped rule, a deleted rule, or a pair whose rules span two groups.">
        <Chip
          size="small"
          variant="outlined"
          label="ungrouped"
          data-triage-group="none"
          sx={{ height: 18, '& .MuiChip-label': { px: 0.75, fontSize: '0.68rem' } }}
        />
      </Tooltip>
    );
  }

  const label = name ?? id;
  return (
    <Tooltip
      title={
        name
          ? `Opener group — open its tab`
          : 'This opener group no longer exists; the id is kept so the card stays traceable.'
      }
    >
      <Chip
        size="small"
        variant="outlined"
        component={RouterLink}
        to={`${OPENER_PAGE}?${GROUP_PARAM}=${encodeURIComponent(id)}`}
        clickable
        label={label}
        data-triage-group={id}
        sx={{
          height: 18,
          maxWidth: 220,
          borderColor: group?.color || undefined,
          '& .MuiChip-label': {
            px: 0.75,
            fontSize: '0.68rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          },
        }}
      />
    </Tooltip>
  );
}
